/** @jsxImportSource @emotion/react */
import { useState, useMemo, useRef, useEffect, MouseEvent } from 'react';
import { useTranslation } from 'react-i18next';
import { Button } from '@mui/material';
import {
  SubTitleLayout,
  SubTitleGroup,
  ControlBtnGroup,
  GlobalBtnGroup,
} from 'components/layouts/ContentLayout';
import {
  SearchBox,
  SearchBoxRow,
  InputBox,
  SearchRows,
  SearchCols,
  SearchButtonWrap,
} from 'components/layouts/SearchBox';
import { ComboBox, MultiComboBox } from 'components/selects/ComboBox';
import { IconButton } from 'components/buttons/IconSVG';
import CustomInputWithSearch from 'components/inputs/CustomInputWithSearch';
import { useMessageBar } from 'hooks/useMessageBar';
import CustomGrid from 'components/grids/CustomGrid';
import { Code } from 'models/common/CommonCode';
import { useCommonModal } from 'hooks/useCommonModal';
import SearchBoxButtons from 'components/buttons/SearchBoxButtons';
import { useLoading } from 'components/process/Loading';
import { MdIvesCondition, MdIvesPrjInfo } from 'models/md/MdIves';
import { ManagementMode, PageMode } from 'models/common/Common';
import { useNavigate } from 'react-router-dom';
import { getCommonCodeNames } from 'apis/admin/CommonCode';
import { copyMdIves, findMdIvesList } from 'apis/md/MdProject';
import { CellMaker } from '@grapecity/wijmo.grid.cellmaker';
import { ICellTemplateContext } from '@grapecity/wijmo.grid';
import { CommonUtil } from 'utils/CommonUtil';
import { MdCustomer } from 'models/md/MdCustomer';
import { findMdCustomerList } from 'apis/md/MdCustomer';
import UserModal from 'components/modals/common/UserModal';
import { hasRole } from 'utils/SessionUtil';

const MdIvesCmpuInqPage = () => {
  const { t } = useTranslation();
  const { openLoading } = useLoading();
  const { openMessageBar } = useMessageBar();
  const { openCommonModal } = useCommonModal();
  const navigate = useNavigate();

  const initCondition: MdIvesCondition = {
    elmPrjId: '',
    elmPrjNm: '',
    bildPlntCd: '',
    bildPlntCds: [],
    elmPrjStatCd: '',
    custNm: '',
    mdlNm: '',
    dataInsUserId: '',
    dataInsUserNm: '',
  };

  const gridRef = useRef<any>();
  const [bildPlntCode, setBildPlntCode] = useState<Code[]>([]);
  const [customerList, setCustomerList] = useState<MdCustomer[]>([]);
  const [elmPrjStatCd, setElmPrjStatCd] = useState<Code[]>([]);
  const [rowData, setRowData] = useState<MdIvesPrjInfo[]>([]);
  const [condition, setCondition] = useState<MdIvesCondition>({ ...initCondition });
  const [isOpenWriter, setIsOpenWriter] = useState(false);

  const customerCodes = useMemo(() => {
    return customerList?.map((o) => ({ cmnCd: o.custId, cmnCdNm: o.custNm }));
  }, [customerList]);

  const layoutDefinition = useMemo(() => {
    return [
      {
        binding: 'bildPlntCd',
        header: String(t('com.label.플랜트', '플랜트')),
        align: 'center',
        minWidth: 40,
        width: 120,
        cellTemplate: (params) => {
          return (params.item?.plants || []).map((o) => o.bildPlntCdNm).join(', ');
        },
      },
      {
        header: String(t('com.label.라인', '라인')),
        columns: [
          {
            binding: 'eltrLnNm',
            header: String(t('com.label.전극', '전극')),
            width: 120,
            isReadOnly: true,
            align: 'center',
            cellTemplate: (params) => {
              return (params.item?.eltrLines || []).map((o) => o.prdnLnNm).join(', ');
            },
          },
          {
            binding: 'cellLnNm',
            header: String(t('com.label.셀', '셀')),
            width: 120,
            isReadOnly: true,
            align: 'center',
            cellTemplate: (params) => {
              return (params.item?.cellLines || []).map((o) => o.prdnLnNm).join(', ');
            },
          },
        ],
      },
      {
        binding: 'custNm',
        header: String(t('com.label.고객사', '고객사')),
        align: 'center',
        minWidth: 40,
        width: 120,
      },
      {
        binding: 'elmPrjNm',
        header: String(t('com.label.프로젝트명', '프로젝트명')),
        align: 'center',
        minWidth: 200,
        width: '*',
        cellTemplate: CellMaker.makeLink({
          click: (e, ctx) => {
            btnDetail(ctx.item.elmPrjId);
          },
        }),
      },
      {
        binding: 'elmPrjId',
        header: String(t('com.label.투자비 검토', '투자비 검토')),
        align: 'center',
        minWidth: 40,
        width: 100,
        cssClass: 'WijmoBasicButton noTooltip',
        cellTemplate: (ctx: ICellTemplateContext) => {
          return `<button data-id='detail'>투자비 검토</button>`;
        },
      },
      {
        binding: 'mdlNm',
        header: String(t('com.label.모델명', '모델명')),
        align: 'center',
        minWidth: 40,
        width: 120,
      },
      {
        binding: 'ivesAmt',
        header: String(t('com.label.투자비', '투자비')),
        align: 'center',
        minWidth: 40,
        width: 120,
        cellTemplate: (ctx: ICellTemplateContext) => {
          return `${ctx.value || 0}${String(t('com.label.억원', '억원'))}`;
        },
      },
      {
        binding: 'elmPrjStatCdNm',
        header: String(t('com.label.상태', '상태')),
        align: 'center',
        minWidth: 40,
        width: 60,
        cssClass: 'WijmoTag',
        cellTemplate: (ctx: ICellTemplateContext) => {
          if (ctx.item.elmPrjStatCd === 'ING') {
            return `<span class="blue">${ctx.value}</span>`;
          } else if (ctx.item.elmPrjStatCd === 'CPLT') {
            return `<span class="green">${ctx.value}</span>`;
          } else if (ctx.item.elmPrjStatCd === 'DROP') {
            return `<span class="red">${ctx.value}</span>`;
          } else {
            return `<span class="grey">${ctx.value}</span>`;
          }
        },
      },
      {
        binding: 'ivesMgr',
        header: String(t('com.label.프로젝트 담당자', '프로젝트 담당자')),
        align: 'center',
        minWidth: 40,
        width: 120,
      },
      {
        binding: 'mktgMgr',
        header: String(t('com.label.마케팅팀 담당자', '마케팅팀 담당자')),
        align: 'center',
        minWidth: 40,
        width: 120,
      },
      {
        binding: 'dataInsDtm',
        header: String(t('com.label.작성일자', '작성일자')),
        align: 'center',
        minWidth: 40,
        width: 120,
      },
      {
        binding: 'dataUpdDtm',
        header: String(t('com.label.수정일자', '수정일자')),
        align: 'center',
        minWidth: 40,
        width: 120,
      },
    ];
  }, [rowData]);

  // 프로젝트 등록/수정 가능여부
  const isRegist = () => {
    return hasRole('MD_IVES_MANAGER');
  };

  const initCodes = async () => {
    const bildPlntCode = await getCommonCodeNames('BILD_PLNT_CD');
    const elmPrjStatCd = await getCommonCodeNames('MD_ELM_PRJ_STAT_CD');
    const customer = await findMdCustomerList({});
    setBildPlntCode(bildPlntCode);
    setElmPrjStatCd(elmPrjStatCd);
    setCustomerList(customer);
  };

  const handleOnChange = (name: string, value: any) => {
    setCondition((prev) => ({ ...prev, [name]: value }));
  };

  const btnSearch = () => {
    // search
    openLoading(true);
    findMdIvesList(condition)
      .then((res) => {
        if (res === null) {
          openMessageBar({
            type: 'error',
            content: t(
              'com.msg.요청 정보 조회 중 오류가 발생했습니다.',
              '요청 정보 조회 중 오류가 발생했습니다.'
            ),
          });
        } else {
          setRowData(
            res.map((o) => ({
              ...o,
              eltrLnNm: o.eltrLines?.map((o) => o.prdnLnNm).join(),
              cellLnNm: o.cellLines?.map((o) => o.prdnLnNm).join(),
              bildPlntCd: o.plants?.map((o) => o.bildPlntCd).join(),
            }))
          );
        }
      })
      .catch((err) => {
        openMessageBar({
          type: 'error',
          content: t(
            'com.msg.요청 정보 조회 중 오류가 발생했습니다.',
            '요청 정보 조회 중 오류가 발생했습니다.'
          ),
        });
      })
      .finally(() => openLoading(false));
  };

  const btnCopy = () => {
    const items = gridRef.current.selectedItems as MdIvesPrjInfo[];
    const elmPrjId = items[0]?.elmPrjId || ''; // 선택된 프로젝트 ID
    if (!elmPrjId) {
      openMessageBar({
        type: 'error',
        content: t('com.msg.선택된 데이터가 없습니다.', '선택된 데이터가 없습니다.'),
      });
      return;
    }

    openCommonModal({
      modalType: 'confirm',
      content: t('md.msg.해당 프로젝트를 복사하시겠습니까?', '해당 프로젝트를 복사하시겠습니까?'),
      yesCallback: async () => {
        openLoading(true);
        copyMdIves(elmPrjId)
          .then((res) => {
            if (res.successOrNot === 'Y') {
              openMessageBar({
                type: 'confirm',
                content: t('com.msg.복사 되었습니다.', '복사 되었습니다.'),
              });
              btnSearch();
            } else {
              openMessageBar({
                type: 'error',
                content: t('com.msg.복사에 실패했습니다.', '복사에 실패했습니다.'),
              });
            }
          })
          .catch((err) => {
            openMessageBar({
              type: 'error',
              content: t('com.msg.복사중 오류가 발생했습니다.', '복사중 오류가 발생했습니다.'),
            });
          })
          .finally(() => openLoading(false));
      },
    });
  };

  const handleDownloadExcel = () => {
    CommonUtil.exportWijmoExcelGrid(
      gridRef.current,
      t('md.label.형개발 투자비 산출 목록', '형개발 투자비 산출 목록'),
      t('md.label.형개발 투자비 산출 목록', '형개발 투자비 산출 목록')
    );
  };

  const btnDetail = (elmPrjId = '', tabIdx = 0) => {
    const searchPageCondition = {
      upprMnuUrl: location.pathname,
      mnuId: `${location.pathname}/${ManagementMode.CREATE}`,
      mnuNm: t('md.label.형개발 투자비 산출 등록', '형개발 투자비 산출 등록'),
      elmPrjId,
      tabIdx,
    };
    navigate('/md/ives-cmpu/detail', { state: { ...searchPageCondition } });
  };

  const onInitialized = (grid) => {
    gridRef.current = grid;
    grid.hostElement.addEventListener('click', (e) => {
      if (grid.rows.length == 0) return;

      const ht = grid.hitTest(e);
      if (ht.row < 0 || ht.col < 0) return;

      if (ht.panel.cellType != 1) return;

      const item = grid.rows[ht.row].dataItem;
      if (e.target instanceof HTMLButtonElement) {
        switch (e.target.dataset.id) {
          case 'detail':
            btnDetail(item.elmPrjId, 1);
            break;
        }
      }
    });
  };

  useEffect(() => {
    initCodes();
    btnSearch();
  }, []);

  return (
    <>
      <SearchBox>
        <SearchBoxRow>
          <InputBox>
            <SearchRows>
              <SearchCols>
                <label>{String(t('md.label.플랜트', '플랜트'))}</label>
                <MultiComboBox
                  id="bildPlntCds"
                  options={bildPlntCode}
                  placeholder={String(t('com.label.선택', '선택'))}
                  defaultValue={condition?.bildPlntCds}
                  onChange={(value) => handleOnChange('bildPlntCds', value)}
                />
              </SearchCols>
              <SearchCols>
                <label>{String(t('md.label.프로젝트명', '프로젝트명'))}</label>
                <CustomInputWithSearch
                  name="elmPrjNm"
                  value={condition.elmPrjNm}
                  placeholder={String(
                    t('com.label.프로젝트를 입력해 주세요.', '프로젝트를 입력해 주세요.')
                  )}
                  onChange={(e) => handleOnChange('elmPrjNm', e.target.value)}
                />
              </SearchCols>
              <SearchCols>
                <label>{t('md.label.상태', '상태')}</label>
                <ComboBox
                  options={elmPrjStatCd}
                  placeholder={String(t('com.label.전체', '전체'))}
                  defaultValue={condition.elmPrjStatCd}
                  msgError={String(t('com.msg.상태를 선택해 주세요.', '상태를 선택해 주세요.'))}
                  onChange={(value) => handleOnChange('elmPrjStatCd', value)}
                />
              </SearchCols>
            </SearchRows>
            <SearchRows>
              <SearchCols>
                <label>{t('md.label.고객사', '고객사')}</label>
                <ComboBox
                  options={customerCodes}
                  placeholder={String(t('com.label.전체', '전체'))}
                  defaultValue={condition?.custId}
                  onChange={(value) => {
                    handleOnChange('custId', value);
                  }}
                />
              </SearchCols>
              <SearchCols>
                <label>{t('md.label.모델명', '모델명')}</label>
                <CustomInputWithSearch
                  name="mdlNm"
                  value={condition.mdlNm}
                  placeholder={String(
                    t('com.label.모델명을 입력해 주세요.', '모델명을 입력해 주세요.')
                  )}
                  onChange={(e) => handleOnChange('mdlNm', e.target.value)}
                />
              </SearchCols>
              <SearchCols>
                <label>{t('md.label.작성자', '작성자')}</label>
                <CustomInputWithSearch
                  name="dataInsUserNm"
                  className="find"
                  value={condition.dataInsUserNm}
                  placeholder={String(
                    t('com.label.작성자를 선택해 주세요.', '작성자를 선택해 주세요.')
                  )}
                  onSearchClick={(e) => setIsOpenWriter(true)}
                />
              </SearchCols>
            </SearchRows>
          </InputBox>
          <SearchButtonWrap>
            <SearchBoxButtons
              defaultCondition={initCondition}
              setReset={setCondition}
              onSearch={() => btnSearch()}
            />
          </SearchButtonWrap>
        </SearchBoxRow>
      </SearchBox>
      <SubTitleLayout>
        <SubTitleGroup>
          <h3>{t('md.label.형개발 투자비 산출 목록', '형개발 투자비 산출 목록')}</h3>
          <span className="total">
            {t('com.label.총', '총')}
            <span>{(rowData || []).length.toLocaleString()}</span>
            {t('com.label.건', '건')}
          </span>
        </SubTitleGroup>
        <ControlBtnGroup>
          {isRegist() && (
            <Button css={IconButton.button} className="icoNone" onClick={btnCopy} disableRipple>
              {t('com.button.복사', '복사')}
            </Button>
          )}
          <Button
            css={IconButton.button}
            className="Exceldown"
            onClick={handleDownloadExcel}
            disableRipple
          >
            {t('com.button.다운로드', '다운로드')}
          </Button>
          <Button
            css={IconButton.button}
            className="refresh"
            onClick={() => btnSearch()}
            disableRipple
          >
            {t('com.button.새로고침', '새로고침')}
          </Button>
        </ControlBtnGroup>
      </SubTitleLayout>
      <CustomGrid
        layoutDefinition={layoutDefinition}
        rowData={rowData}
        isReadOnly={true}
        allowPinning={false}
        rowSelection={'single'}
        initialized={onInitialized}
        height={510}
      />
      {isRegist() && (
        <GlobalBtnGroup>
          <Button
            css={IconButton.button}
            className="create"
            onClick={() => btnDetail()}
            disableRipple
          >
            {t('com.button.신규등록', '신규등록')}
          </Button>
        </GlobalBtnGroup>
      )}

      {isOpenWriter && (
        <UserModal
          open={isOpenWriter}
          close={() => setIsOpenWriter(false)}
          title={t('com.label.작성자 조회', '작성자 조회')}
          condition={{
            roles: ['FA_USER'],
          }}
          defaultUserId={''}
          singleSelect={true}
          onCallback={(callbackData: any) => {
            if (callbackData.userId === '') {
              openMessageBar({
                type: 'warning',
                content: t('com.msg.선택된 사용자가 없습니다.', '선택된 사용자가 없습니다.'),
              });
              return false;
            }
            setCondition((prev) => ({
              ...prev,
              dataInsUserId: callbackData.userId,
              dataInsUserNm: callbackData.empNm,
            }));
          }}
        />
      )}
    </>
  );
};

export default MdIvesCmpuInqPage;
